import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { fileUrl } from "@/lib/api";
import {
  House, MagnifyingGlass, Buildings, UsersThree, CalendarDots, Graph, Flower,
  FileText, UserCircle, SignOut, GraduationCap, List, X, Robot, Detective, Sparkle, ArrowRight, Briefcase, HandHeart, CaretDown,
} from "@phosphor-icons/react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import NotificationBell from "@/components/NotificationBell";
import MessagesBell from "@/components/MessagesBell";
import GlobalSearch from "@/components/GlobalSearch";
import DisplayControls from "@/components/DisplayControls";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EDITION, isEdu, isIndia } from "@/lib/edition";

const links = [
  { to: "/dashboard", label: "Home", icon: House },
  { to: "/ai-search", label: "AI Search", icon: Sparkle },
  { to: "/directory", label: "Directory", icon: MagnifyingGlass },
  { to: "/find", label: "Reconnect", icon: Detective },
  { to: "/institutions", label: "Institutions", icon: Buildings },
  { to: "/groups", label: "Communities", icon: UsersThree },
  { to: "/jobs", label: "Jobs", icon: Briefcase },
  { to: "/mentorship", label: "Mentorship", icon: HandHeart },
  { to: "/memorials", label: "In Memoriam", icon: Flower },
  { to: "/network", label: "Network", icon: Graph },
  { to: "/events", label: "Events", icon: CalendarDots },
  { to: "/resumes", label: "Resume", icon: FileText },
];
// Keep the top bar uncluttered: a few core links inline, the rest under "More".
const PRIMARY = links.filter((l) => ["/dashboard", "/ai-search", "/directory", "/institutions", "/jobs"].includes(l.to));
const MORE = links.filter((l) => !PRIMARY.includes(l));

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  if (!user) return null;
  const initials = (user.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <>
      <Link to="/why-alumnipulse" data-testid="why-topbar-app"
        className="group flex items-center justify-center gap-2 bg-primary px-4 py-1.5 text-center text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90">
        <Sparkle size={13} weight="fill" className="text-secondary" />
        <span>Why {EDITION.brand} — the joy of finding your way back</span>
        <ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5" />
      </Link>
      <header className="sticky top-0 z-50 border-b border-border/60 bg-background/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center gap-4 px-5 py-3">
        <Link to="/dashboard" className="flex items-center gap-2 shrink-0" data-testid="nav-logo">
          {isEdu ? (
            <>
              <img src={EDITION.logo} alt={EDITION.brand} className="h-9 w-9 shrink-0 rounded-lg object-contain" />
              <span className="font-heading text-lg font-bold tracking-tight text-[#1e3a5f] hidden sm:block">IndianEdu<span className="text-[#f97316]">Pulse</span></span>
            </>
          ) : isIndia ? (
            <>
              <img src={EDITION.logo} alt={EDITION.brand} className="h-9 w-9 shrink-0 rounded-lg object-contain" />
              <span className="font-heading text-lg font-semibold tracking-tight hidden sm:block">{EDITION.brand}{EDITION.mark}</span>
            </>
          ) : (
            <>
              <span className="relative block h-9 w-11 shrink-0 overflow-hidden"><img src="/alumnipulse-logo.png" alt="AlumniPulse" className="absolute left-1/2 top-0 max-w-none -translate-x-1/2" style={{ height: "66px" }} /></span>
              <span className="font-heading text-lg font-semibold tracking-tight hidden sm:block">AlumniPulse™</span>
            </>
          )}
        </Link>

        <nav className="ml-2 hidden min-w-0 items-center gap-1 overflow-hidden xl:flex">
          {PRIMARY.map((l) => {
            const active = loc.pathname === l.to;
            return (
              <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`flex items-center gap-2 rounded-full px-3.5 py-2 text-sm transition-colors ${active ? "bg-accent text-accent-foreground font-medium" : "text-muted-foreground hover:bg-accent/60 hover:text-foreground"}`}>
                <l.icon size={17} weight={active ? "fill" : "regular"} /> {l.label}
              </Link>
            );
          })}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="nav-more"
                className={`flex items-center gap-1 rounded-full px-3.5 py-2 text-sm transition-colors ${MORE.some((l) => l.to === loc.pathname) ? "bg-accent text-accent-foreground font-medium" : "text-muted-foreground hover:bg-accent/60 hover:text-foreground"}`}>
                More <CaretDown size={13} weight="bold" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              {MORE.map((l) => (
                <DropdownMenuItem key={l.to} onClick={() => nav(l.to)} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <l.icon className="mr-2" size={16} /> {l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        <div className="ml-auto flex shrink-0 items-center gap-2">
          {user.role === "admin" && (
            <Link to="/admin/crawler" data-testid="nav-admin-crawler"
              className={`hidden items-center gap-2 rounded-full px-3.5 py-2 text-sm transition-colors xl:flex ${loc.pathname === "/admin/crawler" ? "bg-accent text-accent-foreground font-medium" : "text-muted-foreground hover:bg-accent/60 hover:text-foreground"}`}>
              <Robot size={17} /> Crawler
            </Link>
          )}
          {(user.role === "admin" || user.role === "moderator") && (
            <Link to="/admin/content" data-testid="nav-admin-content"
              className={`hidden items-center gap-2 rounded-full px-3.5 py-2 text-sm transition-colors xl:flex ${loc.pathname === "/admin/content" ? "bg-accent text-accent-foreground font-medium" : "text-muted-foreground hover:bg-accent/60 hover:text-foreground"}`}>
              <FileText size={17} /> {user.role === "admin" ? "Content" : "Moderation"}
            </Link>
          )}
          <DisplayControls className="hidden sm:flex" />
          <GlobalSearch className="hidden sm:grid" />
          <MessagesBell />
          <NotificationBell />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 rounded-full border border-border/60 py-1 pl-1 pr-3 hover:bg-accent/60 transition-colors" data-testid="nav-user-menu">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={fileUrl(user.avatar_file_id)} />
                  <AvatarFallback className="bg-secondary text-secondary-foreground text-xs">{initials}</AvatarFallback>
                </Avatar>
                <span className="text-sm font-medium hidden 2xl:block max-w-[120px] truncate">{user.name}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem onClick={() => nav("/profile")} data-testid="menu-profile"><UserCircle className="mr-2" size={16} /> My Profile</DropdownMenuItem>
              <DropdownMenuItem onClick={() => nav("/connections")} data-testid="menu-connections"><UsersThree className="mr-2" size={16} /> Connections</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => { logout(); nav("/"); }} data-testid="menu-logout"><SignOut className="mr-2" size={16} /> Sign out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="ghost" size="icon" className="xl:hidden" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
            {open ? <X size={20} /> : <List size={20} />}
          </Button>
        </div>
      </div>
      {open && (
        <nav className="border-t border-border/60 px-4 py-2 xl:hidden">
          <div className="mb-2 flex items-center justify-between pt-1">
            <GlobalSearch trigger="full" />
          </div>
          <div className="mb-2 flex justify-center"><DisplayControls /></div>
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
              className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-foreground hover:bg-accent">
              <l.icon size={18} /> {l.label}
            </Link>
          ))}
        </nav>
      )}
    </header>
    </>
  );
}
